import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Cloud, ExternalLink } from 'lucide-react';
import { Badge, type BadgeTone } from '../../components/Badge.tsx';
import { Button } from '../../components/Button.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import { CopyButton } from '../../components/CopyButton.tsx';
import { Disclosure } from '../../components/Disclosure.tsx';
import { api, type ServerDomainConnect } from '../onboarding/api.ts';
import { useOnboarding } from '../onboarding/store.ts';
import { message, type Letter } from './model.ts';
import styles from './authorization.module.css';

type Published = 'checking' | 'published' | 'missing' | 'replace' | 'unavailable';

const STATE: Record<Published, { label: string; tone: BadgeTone; shape: string }> = {
  checking: { label: 'Checking your DNS…', tone: 'neutral', shape: '○' },
  published: { label: 'Published', tone: 'positive', shape: '✓' },
  missing: { label: 'Not published yet', tone: 'attention', shape: '△' },
  replace: { label: 'Older letter published', tone: 'attention', shape: '△' },
  unavailable: { label: 'Could not check', tone: 'neutral', shape: '○' },
};

// Reads the customer's own DNS through the domain-letter API: is this exact grant published?
function usePublication(letter: Letter) {
  const [state, setState] = useState<Published>('checking');
  const [error, setError] = useState('');
  const check = useCallback(async () => {
    setState('checking');
    try {
      const found = (await api.domainLetter(letter.terms.domain)).letter;
      setState(!found ? 'missing' : found.grantId === letter.id ? 'published' : 'replace');
      setError('');
    } catch (e) {
      setState('unavailable');
      setError(message(e));
    }
  }, [letter.id, letter.terms.domain]);
  // Resource loading updates state only after the network request settles.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void check();
  }, [check]);
  return { state, error, check };
}

// One-click publication through the DNS provider (Domain Connect), when it serves this zone and
// has onboarded Traceira's template. Any failure just leaves the manual records.
function useOneClick(letterId: string) {
  const session = useOnboarding((s) => s.server.sessionId) ?? '';
  const [link, setLink] = useState<ServerDomainConnect | null>(null);
  useEffect(() => {
    let live = true;
    api
      .authorizationDomainConnect(letterId, session)
      .then((dc) => {
        if (live) setLink(dc);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [letterId, session]);
  return link?.status === 'available' && link.templateApproved && link.applyUrl
    ? { url: link.applyUrl, provider: link.provider?.name || 'your DNS provider' }
    : null;
}

function lead(state: Published, domain: string) {
  switch (state) {
    case 'published':
      return `Your DNS carries this signed letter. Anyone, and Traceira before every action, verifies it from ${domain}. Delete the record to withdraw.`;
    case 'replace':
      return 'Your DNS carries an earlier letter. Replace that TXT value with the one below; Traceira acts only on the letter you approved last.';
    case 'unavailable':
      return 'Your DNS could not be read just now. Check again in a moment.';
    default:
      return 'Traceira acts only once this signed letter is in your own DNS. Add the TXT record below, then check again. DNS changes can take a few minutes to appear.';
  }
}

// The authorization lives on the customer's domain: they publish it, own it and can withdraw it.
export function DomainPublication({
  letter,
  collapsed = false,
}: {
  letter: Letter;
  collapsed?: boolean;
}) {
  const id = useId();
  const records = letter.records ?? [];
  const { state, error, check } = usePublication(letter);
  const oneClick = useOneClick(letter.id);
  if (records.length === 0) return null;
  const status = STATE[state];
  const domain = letter.terms.domain;
  const txt = records.find((r) => r.type === 'TXT');
  const page = records.find((r) => r.type === 'CNAME');
  const badge = (
    <Badge tone={status.tone} shape={status.shape} data-badge={`publication-${state}`}>
      {status.label}
    </Badge>
  );
  const body = (
    <>
      <p className={styles.publicationLead}>{lead(state, domain)}</p>
      {error && <p className={styles.note}>{error}</p>}
      <ol className={styles.publishSteps}>
        {txt && (
          <li>
            <p className={styles.stepHead}>
              <strong>Signed letter</strong>
              <span>
                Required · TXT record · replaces an earlier letter, keep the verification record
              </span>
            </p>
            <RecordRow name={txt.name} value={txt.value} />
          </li>
        )}
        {page && (
          <li>
            <p className={styles.stepHead}>
              <strong>Authorization page</strong>
              <span>Optional · CNAME record, DNS only</span>
            </p>
            <RecordRow name={page.name} value={page.value} />
            {letter.pageUrl && (
              <p className={styles.note}>
                Shows this authorization at <a href={letter.pageUrl}>{letter.pageUrl}</a>.
              </p>
            )}
          </li>
        )}
      </ol>
      <div className={styles.actions}>
        {oneClick && state !== 'published' && (
          <a
            className={buttonClass('primary')}
            href={oneClick.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Cloud size={16} aria-hidden="true" />
            Add it in {oneClick.provider}
            <ExternalLink size={16} aria-hidden="true" />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        )}
        <Button onPress={() => void check()} isDisabled={state === 'checking'}>
          {state === 'checking' ? 'Checking…' : 'Check again'}
        </Button>
        <Link className={buttonClass('tertiary')} to="/verify" search={{ domain }}>
          Public verification
        </Link>
      </div>
    </>
  );
  // The signed-result screen keeps its single-view layout, so it offers this collapsed.
  if (collapsed)
    return (
      <Disclosure
        title={
          <span className={styles.publicationTitle}>
            Publish this authorization on your domain {badge}
          </span>
        }
      >
        {body}
      </Disclosure>
    );
  return (
    <section className={styles.publication} aria-labelledby={id}>
      <div className={styles.header}>
        <div className={styles.intro}>
          <p className={styles.kicker}>On your domain</p>
          <h3 id={id}>Publish this authorization on your domain</h3>
        </div>
        {badge}
      </div>
      {body}
    </section>
  );
}

function RecordRow({ name, value }: { name: string; value: string }) {
  return (
    <dl className={styles.recordList}>
      <div>
        <dt>Name</dt>
        <dd>
          <code className={styles.recordValue}>{name}</code>
          <CopyButton value={name} what="record name" label="Copy" />
        </dd>
      </div>
      <div>
        <dt>Value</dt>
        <dd>
          <code className={styles.recordValue} title={value}>
            {value}
          </code>
          <CopyButton value={value} what="record value" label="Copy" />
        </dd>
      </div>
    </dl>
  );
}
